// One calendar for the AI team: content, ads, scheduled jobs, routine duties
// and deadlines, by month, week or as a list. Picking a day or an event opens
// its details in the panel beside the grid (never a pop-up).
import { NewPostCard } from './NewPostCard'
import { ErrorState } from '@/components/primitives/ErrorState'
import {
  type CalendarEvent,
  type CalendarKind,
  type CalendarPatch,
  addCalendarEvent,
  fetchCalendar,
  updateCalendarEvent
} from '@/lib/ai-calendar'
import { type TeamAgent, fetchTeam } from '@/lib/ai-team'
import { notifyError, queryClient } from '@/lib/query'
import { cn } from '@/lib/utils'
import { CHIP, type Tone, card } from '@/pages/ai-team/hubs'
import {
  field,
  ghostBtn,
  goBtn,
  sectionTitle
} from '@/pages/ai-team/workspace/parts'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  List,
  Loader2,
  Play,
  Send,
  Trash2,
  X
} from 'lucide-react'
import { type FormEvent, type ReactNode, useMemo, useState } from 'react'
import { toast } from 'sonner'

// ---------------------------------------------------------------- dates

type View = 'month' | 'week' | 'list'
const VIEW_KEY = 'xo-ai-calendar-view'
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const LIST_DAYS = 30
const DAY_MS = 86_400_000

const pad = (n: number) => String(n).padStart(2, '0')
const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)
/** Local YYYY-MM-DD (also the bucket key for a day). */
const ymd = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
const hm = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const sameDay = (a: Date, b: Date) => ymd(a) === ymd(b)
/** "YYYY-MM-DD" as a local date (new Date() would read it as UTC midnight). */
const localDate = (s: string) => {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number)
  return new Date(y, m - 1, d)
}
const isDateOnly = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s)
/** Local "YYYY-MM-DD" + "HH:mm" as an ISO string, or null if invalid. */
const toIso = (date: string, time: string) => {
  const d = new Date(`${date}T${time || '09:00'}`)
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

const startOf = (ev: CalendarEvent) =>
  ev.all_day || isDateOnly(ev.start) ? localDate(ev.start) : new Date(ev.start)

/** Every day an event covers (all-day ends are exclusive), capped at 62. */
function daysOf(ev: CalendarEvent): string[] {
  const first = startOfDay(startOf(ev))
  let last = first
  if (ev.end) {
    if (ev.all_day || isDateOnly(ev.end)) {
      last = addDays(localDate(ev.end), -1)
    } else {
      const end = new Date(ev.end)
      const endDay = startOfDay(end)
      // An end at exactly midnight belongs to the day before.
      last = end.getTime() === endDay.getTime() ? addDays(endDay, -1) : endDay
    }
  }
  if (Number.isNaN(last.getTime()) || last < first) last = first
  const out: string[] = []
  for (let d = first; d <= last && out.length < 62; d = addDays(d, 1))
    out.push(ymd(d))
  return out
}

function rangeFor(view: View, cursor: Date) {
  if (view === 'month') {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const from = addDays(first, -first.getDay())
    return { from, count: 42 }
  }
  if (view === 'week')
    return { from: addDays(startOfDay(cursor), -cursor.getDay()), count: 7 }
  return { from: startOfDay(cursor), count: LIST_DAYS }
}

const timeOf = (d: Date) =>
  d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
const longDay = (d: Date) =>
  d.toLocaleDateString([], {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
const shortDay = (d: Date) =>
  d.toLocaleDateString([], { month: 'short', day: 'numeric' })

function rangeLabel(view: View, cursor: Date, from: Date, count: number) {
  if (view === 'month')
    return cursor.toLocaleDateString([], { month: 'long', year: 'numeric' })
  const last = addDays(from, count - 1)
  const year =
    last.getFullYear() !== new Date().getFullYear()
      ? `, ${last.getFullYear()}`
      : ''
  return `${shortDay(from)} – ${shortDay(last)}${year}`
}

const byTime = (a: CalendarEvent, b: CalendarEvent) =>
  Number(b.all_day) - Number(a.all_day) ||
  startOf(a).getTime() - startOf(b).getTime()

// ---------------------------------------------------------------- kinds

/** What the calendar shows: one tab at the top picks it. */
type CalTab = 'all' | 'schedules' | 'content' | 'ads' | 'deadlines'
const TABS: Array<{ id: CalTab; label: string; kinds: CalendarKind[] }> = [
  { id: 'all', label: 'All', kinds: ['content', 'ads', 'job', 'duty', 'deadline', 'done'] },
  { id: 'schedules', label: 'Agent schedules', kinds: ['duty', 'job', 'done'] },
  { id: 'content', label: 'Content', kinds: ['content'] },
  { id: 'ads', label: 'Ads', kinds: ['ads'] },
  { id: 'deadlines', label: 'Deadlines', kinds: ['deadline'] }
]

const KIND_LABEL: Record<CalendarKind, string> = {
  content: 'Content',
  ads: 'Ads',
  job: 'Jobs',
  duty: 'Duties',
  deadline: 'Deadlines',
  done: 'Finished'
}

const KIND_TONE: Record<CalendarKind, Tone> = {
  content: 'sky',
  ads: 'violet',
  job: 'emerald',
  duty: 'neutral',
  deadline: 'red',
  done: 'neutral'
}

const DOT: Record<Tone, string> = {
  emerald: 'bg-emerald-500',
  amber: 'bg-amber-500',
  red: 'bg-red-500',
  sky: 'bg-sky-500',
  violet: 'bg-violet-500',
  indigo: 'bg-indigo-500',
  rose: 'bg-rose-500',
  teal: 'bg-teal-500',
  fuchsia: 'bg-fuchsia-500',
  neutral: 'bg-neutral-400 dark:bg-neutral-500'
}

/** Deadlines are red when due within three days, amber further out. */
function toneOf(ev: CalendarEvent): Tone {
  if (ev.kind === 'deadline') {
    return startOf(ev).getTime() - Date.now() > 3 * DAY_MS ? 'amber' : 'red'
  }
  return KIND_TONE[ev.kind] ?? 'neutral'
}

const CONTENT_STATUS: Record<string, string> = {
  writing: 'bg-amber-500 animate-pulse motion-reduce:animate-none',
  draft: 'bg-neutral-400 dark:bg-neutral-500',
  stuck: 'bg-red-500',
  approved: 'bg-emerald-500',
  published: 'bg-sky-500'
}

const iconBtn =
  'inline-flex size-9 items-center justify-center rounded-full border border-neutral-200 bg-white text-neutral-700 transition-colors hover:bg-neutral-50 motion-reduce:transition-none dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-200 dark:hover:bg-neutral-800'

const label =
  'mb-1.5 block text-xs font-semibold text-neutral-500 dark:text-neutral-400'

const refresh = () =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ['ai-calendar'] }),
    queryClient.invalidateQueries({ queryKey: ['ai-team'] })
  ])

function Spin({ on, icon }: { on: boolean; icon: ReactNode }) {
  return on ? (
    <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
  ) : (
    <>{icon}</>
  )
}

function readView(): View {
  try {
    const v = localStorage.getItem(VIEW_KEY)
    if (v === 'month' || v === 'week' || v === 'list') return v
  } catch {
    // storage blocked: fall back to month
  }
  return 'month'
}

// ---------------------------------------------------------------- component

type Selection = { day: Date; eventId: string | null }

export function TeamCalendar({
  layers,
  title
}: { layers?: string[]; title?: string }) {
  const [view, setViewState] = useState<View>(readView)
  const [cursor, setCursor] = useState(() => startOfDay(new Date()))
  const [selection, setSelection] = useState<Selection>(() => ({
    day: startOfDay(new Date()),
    eventId: null
  }))
  const [tab, setTab] = useState<CalTab>('all')
  const tabKinds = TABS.find((t) => t.id === tab)!.kinds
  const [agentKey, setAgentKey] = useState('')
  const [showDone, setShowDone] = useState(false)

  const { from, count } = rangeFor(view, cursor)
  const fromStr = ymd(from)
  const toStr = ymd(addDays(from, count))
  const layerKey = layers?.join(',')

  const query = useQuery({
    queryKey: ['ai-calendar', layerKey, fromStr, toStr, showDone],
    queryFn: () =>
      fetchCalendar({ layers, from: fromStr, to: toStr, done: showDone }),
    refetchInterval: 30_000,
    placeholderData: (prev) => prev
  })
  const team = useQuery({
    queryKey: ['ai-team'],
    queryFn: fetchTeam,
    staleTime: 60_000
  })

  const agents = useMemo(
    () =>
      (team.data?.agents ?? []).filter(
        (a) => !layers?.length || layers.includes(a.layer)
      ),
    // layerKey stands in for the layers array (a new array each render)
    [team.data, layerKey]
  )

  const allEvents = query.data?.events ?? []
  const events = useMemo(
    () =>
      allEvents.filter(
        (e) =>
          tabKinds.includes(e.kind) &&
          (showDone || e.kind !== 'done') &&
          (!agentKey || e.agent?.key === agentKey)
      ),
    [allEvents, tabKinds, showDone, agentKey]
  )

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const ev of events) {
      for (const key of daysOf(ev)) map.set(key, [...(map.get(key) ?? []), ev])
    }
    for (const list of map.values()) list.sort(byTime)
    return map
  }, [events])

  const days = Array.from({ length: count }, (_, i) => addDays(from, i))
  const selectedEvent = selection.eventId
    ? (allEvents.find((e) => e.id === selection.eventId) ?? null)
    : null

  const setView = (v: View) => {
    setViewState(v)
    setCursor(selection.day)
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      // storage blocked: the view just won't be remembered
    }
  }

  const shift = (dir: 1 | -1) =>
    setCursor((c) =>
      view === 'month'
        ? new Date(c.getFullYear(), c.getMonth() + dir, 1)
        : addDays(c, dir * (view === 'week' ? 7 : LIST_DAYS))
    )

  const goToday = () => {
    const today = startOfDay(new Date())
    setCursor(today)
    setSelection({ day: today, eventId: null })
  }

  const pickDay = (day: Date) => setSelection({ day, eventId: null })
  const pickEvent = (ev: CalendarEvent, day?: Date) =>
    setSelection({ day: day ?? startOfDay(startOf(ev)), eventId: ev.id })

  const label_ = rangeLabel(view, cursor, from, count)

  return (
    <section className="space-y-4">
      {/* What to see */}
      <div role="tablist" aria-label="What to show" className="flex gap-1 overflow-x-auto rounded-full border border-neutral-200 bg-white p-1 [scrollbar-width:none] dark:border-neutral-800 dark:bg-neutral-900">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              'shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-colors motion-reduce:transition-none',
              tab === t.id
                ? 'bg-emerald-500 text-neutral-950'
                : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <button
            type="button"
            onClick={goToday}
            className={cn(ghostBtn, 'px-4 py-2')}
          >
            Today
          </button>
          <button
            type="button"
            aria-label={`Previous ${view === 'list' ? '30 days' : view}`}
            onClick={() => shift(-1)}
            className={iconBtn}
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label={`Next ${view === 'list' ? '30 days' : view}`}
            onClick={() => shift(1)}
            className={iconBtn}
          >
            <ChevronRight className="size-4" />
          </button>
          <h2 className={cn(sectionTitle, 'ml-1 truncate')} aria-live="polite">
            {title ? (
              <span className="text-neutral-500 dark:text-neutral-400">
                {title} ·{' '}
              </span>
            ) : null}
            {label_}
          </h2>
          {query.isFetching && !query.isLoading && (
            <Loader2
              aria-label="Refreshing"
              className="size-4 animate-spin text-neutral-400 motion-reduce:animate-none"
            />
          )}
        </div>
        <div
          role="group"
          aria-label="Calendar view"
          className="flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1 dark:border-neutral-800 dark:bg-neutral-900"
        >
          {(
            [
              ['month', 'Month', CalendarDays],
              ['week', 'Week', Clock],
              ['list', 'List', List]
            ] as const
          ).map(([v, text, Icon]) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => setView(v)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none',
                view === v
                  ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                  : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
              )}
            >
              <Icon className="size-3.5" />
              {text}
            </button>
          ))}
        </div>
      </div>

      {/* Filters + legend */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          aria-label="Filter by agent"
          value={agentKey}
          onChange={(e) => setAgentKey(e.target.value)}
          className={cn(field, 'w-auto py-1.5 text-xs')}
        >
          <option value="">All agents</option>
          {agents.map((a) => (
            <option key={a.key} value={a.key}>
              {a.name}
            </option>
          ))}
        </select>
        <label className="inline-flex cursor-pointer items-center gap-2 text-xs font-semibold text-neutral-600 dark:text-neutral-300">
          <input
            type="checkbox"
            checked={showDone}
            onChange={(e) => setShowDone(e.target.checked)}
            className="size-4 accent-emerald-500"
          />
          Show finished work
        </label>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-neutral-500 dark:text-neutral-400">
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-red-500" /> Due within 3
          days
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-amber-500" /> Deadline later
          / being written
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-neutral-400" /> Draft
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-emerald-500" /> Approved
        </span>
        <span className="flex items-center gap-1.5">
          <span className="size-1.5 rounded-full bg-sky-500" /> Published
        </span>
      </div>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0">
          {query.isLoading ? (
            <CalendarSkeleton view={view} />
          ) : query.isError && !query.data ? (
            <ErrorState
              error={query.error}
              onRetry={() => query.refetch()}
              className="rounded-[36px]"
              title="Couldn't load the calendar"
            />
          ) : (
            <>
              {query.isError && (
                <p
                  role="status"
                  className="mb-3 flex flex-wrap items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2 text-xs font-semibold text-amber-700 dark:text-amber-300"
                >
                  Couldn't refresh the calendar. Showing what was loaded before.
                  <button
                    type="button"
                    onClick={() => query.refetch()}
                    className="rounded-full px-2 py-0.5 underline underline-offset-2 hover:bg-amber-500/10"
                  >
                    Try again
                  </button>
                </p>
              )}
              {query.data?.truncated && (
                <p className="mb-3 rounded-full border border-neutral-200 px-4 py-2 text-xs text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                  Showing the first 600 items — switch to Week or a tab to see the
                  rest
                </p>
              )}
              {events.length === 0 && (
                <p className="mb-3 rounded-[36px] border border-dashed border-neutral-200 px-5 py-3 text-sm text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
                  {allEvents.length
                    ? 'Nothing matches these filters in this range.'
                    : 'Nothing planned in this range yet. Pick a day to schedule a job or plan content.'}
                </p>
              )}
              {view === 'month' && (
                <MonthGrid
                  days={days}
                  month={cursor.getMonth()}
                  byDay={byDay}
                  selection={selection}
                  onDay={pickDay}
                  onEvent={pickEvent}
                />
              )}
              {view === 'week' && (
                <WeekGrid
                  days={days}
                  byDay={byDay}
                  selection={selection}
                  onDay={pickDay}
                  onEvent={pickEvent}
                />
              )}
              {view === 'list' && (
                <ListView
                  days={days}
                  byDay={byDay}
                  selection={selection}
                  onDay={pickDay}
                  onEvent={pickEvent}
                />
              )}
            </>
          )}
        </div>

        <aside
          className={cn(card, 'h-fit space-y-4')}
          aria-label="Calendar details"
        >
          {selectedEvent ? (
            <EventPanel
              key={`${selectedEvent.id}:${selectedEvent.status}`}
              event={selectedEvent}
              onBack={() => setSelection((s) => ({ ...s, eventId: null }))}
            />
          ) : (
            <DayPanel
              key={ymd(selection.day)}
              day={selection.day}
              events={byDay.get(ymd(selection.day)) ?? []}
              agents={agents}
              tab={tab}
              onEvent={(ev) => pickEvent(ev, selection.day)}
            />
          )}
        </aside>
      </div>
    </section>
  )
}

// ---------------------------------------------------------------- grid pieces

interface GridProps {
  days: Date[]
  byDay: Map<string, CalendarEvent[]>
  selection: Selection
  onDay: (day: Date) => void
  onEvent: (ev: CalendarEvent, day: Date) => void
}

function eventAria(ev: CalendarEvent) {
  const when = ev.all_day ? 'all day' : timeOf(startOf(ev))
  return `${KIND_LABEL[ev.kind]}: ${ev.title}, ${when}${ev.agent ? `, ${ev.agent.name}` : ''}${
    ev.status_label ? `, ${ev.status_label}` : ''
  }`
}

function EventPill({
  ev,
  selected,
  onClick,
  roomy
}: {
  ev: CalendarEvent
  selected: boolean
  onClick: () => void
  roomy?: boolean
}) {
  const tone = toneOf(ev)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={eventAria(ev)}
      aria-pressed={selected}
      title={ev.title}
      className={cn(
        'flex w-full min-w-0 items-center gap-1.5 rounded-full px-2 py-1 text-left text-[11px] font-semibold transition-shadow motion-reduce:transition-none',
        CHIP[tone],
        ev.kind === 'done' && 'opacity-60',
        selected && 'ring-2 ring-neutral-900 dark:ring-white',
        roomy && 'py-1.5 text-xs'
      )}
    >
      <span className={cn('size-1.5 shrink-0 rounded-full', DOT[tone])} />
      {!ev.all_day && (
        <span className="shrink-0 tabular-nums opacity-80">
          {timeOf(startOf(ev))}
        </span>
      )}
      <span className="min-w-0 flex-1 truncate">{ev.title}</span>
      {ev.kind === 'content' && CONTENT_STATUS[ev.status] && (
        <span
          aria-hidden
          className={cn(
            'size-2 shrink-0 rounded-full ring-2 ring-white dark:ring-neutral-900',
            CONTENT_STATUS[ev.status]
          )}
        />
      )}
    </button>
  )
}

function dayAria(day: Date, n: number) {
  return `${longDay(day)}, ${n ? `${n} item${n === 1 ? '' : 's'}` : 'nothing planned'}`
}

function DayNumber({
  day,
  isToday,
  selected,
  onClick,
  n
}: {
  day: Date
  isToday: boolean
  selected: boolean
  onClick: () => void
  n: number
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={dayAria(day, n)}
      aria-pressed={selected}
      aria-current={isToday ? 'date' : undefined}
      className={cn(
        'inline-flex size-7 items-center justify-center rounded-full text-xs font-bold tabular-nums transition-colors motion-reduce:transition-none',
        isToday
          ? 'bg-emerald-500 text-neutral-950'
          : selected
            ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
            : 'text-neutral-700 hover:bg-neutral-100 dark:text-neutral-200 dark:hover:bg-neutral-800'
      )}
    >
      {day.getDate()}
    </button>
  )
}

const gridShell =
  'min-w-[760px] overflow-hidden rounded-[36px] border border-neutral-200 bg-white dark:border-neutral-800 dark:bg-neutral-900'

function WeekdayHeader() {
  return (
    <div className="grid grid-cols-7 border-b border-neutral-200 dark:border-neutral-800">
      {WEEKDAYS.map((d) => (
        <p
          key={d}
          className="py-2.5 text-center text-xs font-semibold text-neutral-500 dark:text-neutral-400"
        >
          {d}
        </p>
      ))}
    </div>
  )
}

function MonthGrid({
  days,
  month,
  byDay,
  selection,
  onDay,
  onEvent
}: GridProps & { month: number }) {
  const today = new Date()
  return (
    <div className="overflow-x-auto">
      <div className={gridShell}>
        <WeekdayHeader />
        <div className="grid grid-cols-7">
          {days.map((day, i) => {
            const key = ymd(day)
            const list = byDay.get(key) ?? []
            const isToday = sameDay(day, today)
            const selected = sameDay(day, selection.day)
            const outside = day.getMonth() !== month
            return (
              <div
                key={key}
                className={cn(
                  'flex min-h-[120px] min-w-0 flex-col gap-1 border-neutral-200 p-1.5 dark:border-neutral-800',
                  i % 7 && 'border-l',
                  i >= 7 && 'border-t',
                  outside && 'bg-neutral-50/60 dark:bg-neutral-950/40',
                  selected && 'bg-neutral-50 dark:bg-neutral-800/50',
                  isToday &&
                    'bg-emerald-500/5 ring-2 ring-inset ring-emerald-500/40'
                )}
              >
                <div
                  className={cn(
                    'flex items-center justify-between',
                    outside && 'opacity-60'
                  )}
                >
                  <DayNumber
                    day={day}
                    isToday={isToday}
                    selected={selected}
                    onClick={() => onDay(day)}
                    n={list.length}
                  />
                </div>
                {list.slice(0, 3).map((ev) => (
                  <EventPill
                    key={`${ev.id}-${key}`}
                    ev={ev}
                    selected={selection.eventId === ev.id}
                    onClick={() => onEvent(ev, day)}
                  />
                ))}
                {list.length > 3 && (
                  <button
                    type="button"
                    onClick={() => onDay(day)}
                    aria-label={`Show all ${list.length} items on ${longDay(day)}`}
                    className="self-start rounded-full px-2 py-0.5 text-[11px] font-semibold text-neutral-500 hover:bg-neutral-100 dark:text-neutral-400 dark:hover:bg-neutral-800"
                  >
                    +{list.length - 3} more
                  </button>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

function WeekGrid({ days, byDay, selection, onDay, onEvent }: GridProps) {
  const today = new Date()
  return (
    <div className="overflow-x-auto">
      <div className={cn(gridShell, 'grid grid-cols-7')}>
        {days.map((day, i) => {
          const key = ymd(day)
          const list = byDay.get(key) ?? []
          const isToday = sameDay(day, today)
          const selected = sameDay(day, selection.day)
          const allDay = list.filter((e) => e.all_day)
          const timed = list.filter((e) => !e.all_day)
          return (
            <div
              key={key}
              className={cn(
                'flex min-h-[420px] min-w-0 flex-col gap-1.5 border-neutral-200 p-2 dark:border-neutral-800',
                i && 'border-l',
                selected && 'bg-neutral-50 dark:bg-neutral-800/50',
                isToday &&
                  'bg-emerald-500/5 ring-2 ring-inset ring-emerald-500/40'
              )}
            >
              <div className="mb-1 flex flex-col items-center gap-1">
                <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
                  {WEEKDAYS[day.getDay()]}
                </span>
                <DayNumber
                  day={day}
                  isToday={isToday}
                  selected={selected}
                  onClick={() => onDay(day)}
                  n={list.length}
                />
              </div>
              {allDay.length > 0 && (
                <div className="space-y-1 border-b border-neutral-200 pb-1.5 dark:border-neutral-800">
                  {allDay.map((ev) => (
                    <EventPill
                      key={ev.id}
                      ev={ev}
                      selected={selection.eventId === ev.id}
                      onClick={() => onEvent(ev, day)}
                    />
                  ))}
                </div>
              )}
              {timed.map((ev) => (
                <EventPill
                  key={ev.id}
                  ev={ev}
                  selected={selection.eventId === ev.id}
                  onClick={() => onEvent(ev, day)}
                />
              ))}
              {list.length === 0 && (
                <p className="mt-2 text-center text-[11px] text-neutral-400 dark:text-neutral-500">
                  Free
                </p>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListView({ days, byDay, selection, onDay, onEvent }: GridProps) {
  const filled = days.filter((d) => (byDay.get(ymd(d)) ?? []).length > 0)
  const today = new Date()
  if (filled.length === 0) {
    return (
      <div
        className={cn(
          card,
          'py-12 text-center text-sm text-neutral-500 dark:text-neutral-400'
        )}
      >
        Nothing in the next {LIST_DAYS} days.
      </div>
    )
  }
  return (
    <ol className="space-y-3">
      {filled.map((day) => {
        const key = ymd(day)
        const list = byDay.get(key) ?? []
        const isToday = sameDay(day, today)
        return (
          <li
            key={key}
            className={cn(card, 'p-4', isToday && 'ring-2 ring-emerald-500/40')}
          >
            <button
              type="button"
              onClick={() => onDay(day)}
              aria-label={dayAria(day, list.length)}
              aria-pressed={sameDay(day, selection.day) && !selection.eventId}
              className="mb-2 flex items-center gap-2 rounded-full pr-2 text-sm font-bold text-neutral-900 hover:text-neutral-600 dark:text-white dark:hover:text-neutral-300"
            >
              {longDay(day)}
              {isToday && (
                <span
                  className={cn(
                    'rounded-full px-2 py-0.5 text-[10px]',
                    CHIP.emerald
                  )}
                >
                  Today
                </span>
              )}
            </button>
            <div className="space-y-1.5">
              {list.map((ev) => (
                <EventPill
                  key={`${ev.id}-${key}`}
                  ev={ev}
                  roomy
                  selected={selection.eventId === ev.id}
                  onClick={() => onEvent(ev, day)}
                />
              ))}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function CalendarSkeleton({ view }: { view: View }) {
  const block =
    'animate-pulse rounded-[36px] bg-neutral-100 motion-reduce:animate-none dark:bg-neutral-800'
  if (view === 'list') {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading calendar">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={cn(block, 'h-28')} />
        ))}
      </div>
    )
  }
  const n = view === 'month' ? 42 : 7
  return (
    <div
      className="overflow-x-auto"
      aria-busy="true"
      aria-label="Loading calendar"
    >
      <div className="grid min-w-[760px] grid-cols-7 gap-1.5">
        {Array.from({ length: n }, (_, i) => (
          <div
            key={i}
            className={cn(block, view === 'month' ? 'h-[120px]' : 'h-[420px]')}
          />
        ))}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------- side panel

function DayPanel({
  day,
  events,
  agents,
  tab: viewing,
  onEvent
}: {
  day: Date
  events: CalendarEvent[]
  agents: TeamAgent[]
  tab: CalTab
  onEvent: (ev: CalendarEvent) => void
}) {
  // Only what fits the tab you're on: content plans posts, agent schedules plan jobs.
  const offers: Array<'post' | 'job'> =
    viewing === 'content' ? ['post'] : viewing === 'schedules' ? ['job'] : viewing === 'all' ? ['post', 'job'] : []
  const [picked, setTab] = useState<'post' | 'job'>(offers[0] ?? 'post')
  const tab = offers.includes(picked) ? picked : offers[0]
  const past = day < startOfDay(new Date())
  return (
    <>
      <div>
        <p className="text-xs font-semibold text-neutral-500 dark:text-neutral-400">
          Selected day
        </p>
        <h3 className={sectionTitle}>{longDay(day)}</h3>
      </div>
      {events.length ? (
        <div className="space-y-1.5">
          {events.map((ev) => (
            <EventPill
              key={ev.id}
              ev={ev}
              roomy
              selected={false}
              onClick={() => onEvent(ev)}
            />
          ))}
        </div>
      ) : (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Nothing planned for this day.
        </p>
      )}

      {past ? (
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          This day has passed, so nothing new can be scheduled on it.
        </p>
      ) : offers.length === 0 ? null : (
        <>
          {offers.length > 1 && (
          <div
            role="tablist"
            aria-label="Add to this day"
            className="flex gap-1 rounded-full border border-neutral-200 p-1 dark:border-neutral-800"
          >
            {(
              [
                ['post', 'Plan a post'],
                ['job', 'Schedule a job']
              ] as const
            ).filter(([t]) => offers.includes(t)).map(([t, text]) => (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={tab === t}
                onClick={() => setTab(t)}
                className={cn(
                  'flex-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors motion-reduce:transition-none',
                  tab === t
                    ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
                    : 'text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                )}
              >
                {text}
              </button>
            ))}
          </div>
          )}
          {offers.length === 1 && (
            <p className="text-sm font-bold text-neutral-900 dark:text-white">
              {tab === 'job' ? 'Schedule a job' : 'Plan a post'}
            </p>
          )}
          {tab === 'job' ? (
            <JobForm day={day} agents={agents} />
          ) : (
            <NewPostCard key={ymd(day)} compact at={`${ymd(day)}T${defaultTime(day)}`} />
          )}
        </>
      )}
    </>
  )
}

/** Default time for a new item: 9am, or the next whole hour when that's passed today. */
function defaultTime(day: Date) {
  const now = new Date()
  if (!sameDay(day, now) || now.getHours() < 9) return '09:00'
  // Late in the evening there's no next whole hour today: take the last minute.
  if (now.getHours() >= 23) return '23:59'
  return `${pad(now.getHours() + 1)}:00`
}

function futureIso(day: Date | string, time: string) {
  const iso = toIso(typeof day === 'string' ? day : ymd(day), time)
  if (!iso) {
    toast.error('Pick a valid time')
    return null
  }
  if (new Date(iso).getTime() < Date.now() - 60_000) {
    toast.error('Pick a time in the future')
    return null
  }
  return iso
}

function JobForm({ day, agents }: { day: Date; agents: TeamAgent[] }) {
  const groups = agents
    .map((a) => ({
      agent: a,
      jobs: a.quick_tasks.filter((j) => j.enabled !== false)
    }))
    .filter((g) => g.jobs.length > 0)
  const jobs = groups.flatMap((g) => g.jobs)
  const [jobId, setJobId] = useState('')
  const [choice, setChoice] = useState('')
  const [note, setNote] = useState('')
  const [time, setTime] = useState(() => defaultTime(day))
  const [pending, setPending] = useState(false)
  const job = jobs.find((j) => String(j.id) === jobId)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!job) return toast.error('Pick a job')
    if (job.choice && !choice)
      return toast.error(`Pick ${job.choice.label.toLowerCase()}`)
    const at = futureIso(day, time)
    if (!at) return
    setPending(true)
    try {
      await addCalendarEvent({
        type: 'job',
        job: job.id,
        at,
        ...(job.choice ? { choice } : {}),
        ...(note.trim() ? { note: note.trim() } : {})
      })
      toast.success('Job scheduled', {
        description: `${job.label} on ${shortDay(day)} at ${time}`
      })
      setJobId('')
      setChoice('')
      setNote('')
      await refresh()
    } catch (err) {
      notifyError("Couldn't schedule the job", err)
    } finally {
      setPending(false)
    }
  }

  if (!groups.length) {
    return (
      <p className="text-sm text-neutral-500 dark:text-neutral-400">
        No jobs are set up for this team yet.
      </p>
    )
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label htmlFor="cal-job" className={label}>
          Job
        </label>
        <select
          id="cal-job"
          value={jobId}
          onChange={(e) => {
            setJobId(e.target.value)
            setChoice('')
          }}
          className={field}
        >
          <option value="">Choose a job…</option>
          {groups.map((g) => (
            <optgroup key={g.agent.key} label={g.agent.name}>
              {g.jobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      {job?.choice && (
        <div>
          <label htmlFor="cal-choice" className={label}>
            {job.choice.label}
          </label>
          <select
            id="cal-choice"
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            className={field}
          >
            <option value="">Choose…</option>
            {job.choice.options.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="cal-note" className={label}>
          Note (optional)
        </label>
        <textarea
          id="cal-note"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Anything the agent should know"
          className={cn(field, 'resize-none')}
        />
      </div>
      <div>
        <label htmlFor="cal-job-time" className={label}>
          Time
        </label>
        <input
          id="cal-job-time"
          type="time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          className={field}
        />
      </div>
      <button
        type="submit"
        disabled={pending || !job}
        className={cn(goBtn, 'w-full')}
      >
        <Spin on={pending} icon={<Clock className="size-4" />} />
        Schedule job
      </button>
    </form>
  )
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <dt className="shrink-0 text-neutral-500 dark:text-neutral-400">{k}</dt>
      <dd className="min-w-0 text-right font-semibold text-neutral-900 dark:text-white">
        {v}
      </dd>
    </div>
  )
}

function EventPanel({
  event,
  onBack
}: { event: CalendarEvent; onBack: () => void }) {
  const start = startOf(event)
  const tone = toneOf(event)
  const [date, setDate] = useState(ymd(start))
  const [time, setTime] = useState(event.all_day ? '09:00' : hm(start))
  const [title, setTitle] = useState(event.title)
  const [body, setBody] = useState(event.body ?? '')
  // The poll can bring a newer title/body (e.g. the agent finished writing):
  // take it, but never over what the admin has typed.
  const [seen, setSeen] = useState({ title: event.title, body: event.body ?? '' })
  if (seen.title !== event.title || seen.body !== (event.body ?? '')) {
    if (title === seen.title) setTitle(event.title)
    if (body === seen.body) setBody(event.body ?? '')
    setSeen({ title: event.title, body: event.body ?? '' })
  }
  const [busy, setBusy] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<'discard' | 'cancel' | null>(null)

  const id = event.ref.id
  const canAct = event.editable && id !== null
  const isDraft = event.ref.type === 'draft'
  const isRoutine = event.ref.type === 'routine'
  const isSocial = event.ref.type === 'social'
  const writing = event.status === 'writing'
  // Only a one-time job can be moved; a repeating one changes in Settings.
  const once = event.status_label === 'Once'
  const canMove =
    canAct &&
    (isDraft ||
      (isRoutine && once) ||
      (isSocial && event.status === 'scheduled') ||
      event.ref.type === 'adplan')
  const dirty = title !== event.title || body !== (event.body ?? '')
  const edits = dirty ? { title: title.trim() || event.title, body } : {}
  const decide = (key: 'approve' | 'publish') => {
    if (!body.trim()) return toast.error('Write the draft first')
    void act(
      key,
      { status: key === 'approve' ? 'approved' : 'published', ...edits },
      key === 'approve' ? 'Approved' : 'Published'
    )
  }

  const act = async (
    key: string,
    patch: CalendarPatch,
    success: string,
    after?: () => void
  ) => {
    if (id === null) return
    setBusy(key)
    try {
      await updateCalendarEvent({ type: event.ref.type, id }, patch)
      toast.success(success)
      setConfirm(null)
      after?.()
      await refresh()
    } catch (err) {
      notifyError("Couldn't update it", err)
    } finally {
      setBusy(null)
    }
  }

  const reschedule = (e: FormEvent) => {
    e.preventDefault()
    // Ad plans only take the day; everything else must be in the future.
    const at =
      event.ref.type === 'adplan' ? toIso(date, time) : futureIso(date, time)
    if (!at) {
      if (event.ref.type === 'adplan') toast.error('Pick a valid date')
      return
    }
    void act('move', { at }, 'Rescheduled')
  }

  const when = event.all_day
    ? `${longDay(start)} · all day`
    : `${longDay(start)} · ${timeOf(start)}${event.end && !isDateOnly(event.end) ? ` – ${timeOf(new Date(event.end))}` : ''}`

  return (
    <>
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 rounded-full text-xs font-semibold text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white"
      >
        <ArrowLeft className="size-3.5" /> Back to {shortDay(start)}
      </button>
      <div>
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold',
            CHIP[tone]
          )}
        >
          <span className={cn('size-1.5 rounded-full', DOT[tone])} />
          {KIND_LABEL[event.kind]}
        </span>
        <h3 className={cn(sectionTitle, 'mt-2 break-words')}>{event.title}</h3>
      </div>
      <dl className="space-y-2">
        <Row k="When" v={when} />
        {event.agent && <Row k="Agent" v={event.agent.name} />}
        {event.status_label && (
          <Row
            k="Status"
            v={
              <span className="inline-flex items-center gap-1.5">
                {event.kind === 'content' && CONTENT_STATUS[event.status] && (
                  <span
                    className={cn(
                      'size-2 rounded-full',
                      CONTENT_STATUS[event.status]
                    )}
                  />
                )}
                {event.status_label}
              </span>
            }
          />
        )}
      </dl>
      {event.details && (
        <p className="whitespace-pre-wrap break-words text-sm text-neutral-600 dark:text-neutral-300">
          {event.details}
        </p>
      )}

      {isDraft && canAct && (
        <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <label htmlFor="cal-dtitle" className={label}>
            Title
          </label>
          <input
            id="cal-dtitle"
            value={title}
            disabled={writing}
            onChange={(e) => setTitle(e.target.value)}
            className={cn(field, 'disabled:opacity-50')}
          />
          <label htmlFor="cal-dbody" className={label}>
            Draft
          </label>
          {writing ? (
            <p className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
              <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />{' '}
              The agent is still writing this.
            </p>
          ) : (
            <textarea
              id="cal-dbody"
              rows={8}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className={cn(field, 'resize-y')}
            />
          )}
          {!writing && (
            <button
              type="button"
              disabled={busy !== null || !dirty}
              onClick={() =>
                act(
                  'save',
                  { title: title.trim() || event.title, body },
                  'Draft saved'
                )
              }
              className={cn(ghostBtn, 'w-full')}
            >
              <Spin on={busy === 'save'} icon={<Check className="size-4" />} />
              Save changes
            </button>
          )}
          {!writing && (
            <div className="flex flex-wrap gap-2">
              {event.status !== 'approved' && event.status !== 'published' && (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => decide('approve')}
                  className={cn(goBtn, 'flex-1 px-4')}
                >
                  <Spin
                    on={busy === 'approve'}
                    icon={<Check className="size-4" />}
                  />{' '}
                  Approve
                </button>
              )}
              {event.status !== 'published' && (
                <button
                  type="button"
                  disabled={busy !== null}
                  onClick={() => decide('publish')}
                  className={cn(ghostBtn, 'flex-1 px-4')}
                >
                  <Spin
                    on={busy === 'publish'}
                    icon={<Send className="size-4" />}
                  />{' '}
                  Publish
                </button>
              )}
            </div>
          )}
          {event.status !== 'published' && (
            <ConfirmButton
              armed={confirm === 'discard'}
              busy={busy === 'discard'}
              disabled={busy !== null}
              idle="Discard"
              armedText="Click again to discard"
              icon={<Trash2 className="size-4" />}
              onArm={() => setConfirm('discard')}
              onDisarm={() => setConfirm(null)}
              onConfirm={() =>
                act('discard', { status: 'rejected' }, 'Discarded', onBack)
              }
            />
          )}
        </div>
      )}

      {isRoutine && canAct && (
        <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <button
            type="button"
            disabled={busy !== null}
            onClick={() => act('run', { run_now: true }, 'Started')}
            className={cn(goBtn, 'w-full')}
          >
            <Spin on={busy === 'run'} icon={<Play className="size-4" />} /> Run
            now
          </button>
          <ConfirmButton
            armed={confirm === 'cancel'}
            busy={busy === 'cancel'}
            disabled={busy !== null}
            idle={once ? 'Cancel this job' : 'Stop this schedule'}
            armedText={once ? 'Click again to cancel it' : 'Click again to stop it'}
            icon={<X className="size-4" />}
            onArm={() => setConfirm('cancel')}
            onDisarm={() => setConfirm(null)}
            onConfirm={() =>
              act('cancel', { cancel: true }, once ? 'Cancelled' : 'Schedule stopped', onBack)
            }
          />
          {!once && (
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              This job repeats. Change its time in Settings → Schedules.
            </p>
          )}
        </div>
      )}

      {isSocial && canAct && event.status === 'scheduled' && (
        <div className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800">
          <ConfirmButton
            armed={confirm === 'cancel'}
            busy={busy === 'cancel'}
            disabled={busy !== null}
            idle="Cancel this post"
            armedText="Click again to cancel it"
            icon={<X className="size-4" />}
            onArm={() => setConfirm('cancel')}
            onDisarm={() => setConfirm(null)}
            onConfirm={() =>
              act('cancel', { status: 'rejected' }, 'Post cancelled', onBack)
            }
          />
        </div>
      )}

      {canMove && (
        <form
          onSubmit={reschedule}
          className="space-y-2 border-t border-neutral-200 pt-4 dark:border-neutral-800"
        >
          <p className="text-sm font-bold text-neutral-900 dark:text-white">
            Reschedule
          </p>
          <div className="grid grid-cols-2 gap-2">
            <input
              aria-label="New date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className={field}
            />
            <input
              aria-label="New time"
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className={field}
            />
          </div>
          <button
            type="submit"
            disabled={busy !== null}
            className={cn(ghostBtn, 'w-full')}
          >
            <Spin
              on={busy === 'move'}
              icon={<CalendarDays className="size-4" />}
            />{' '}
            Move
          </button>
        </form>
      )}

      {!canAct && (
        <p className="border-t border-neutral-200 pt-4 text-xs text-neutral-500 dark:border-neutral-800 dark:text-neutral-400">
          This item can't be changed from the calendar.
        </p>
      )}
    </>
  )
}

function ConfirmButton({
  armed,
  busy,
  disabled,
  idle,
  armedText,
  icon,
  onArm,
  onDisarm,
  onConfirm
}: {
  armed: boolean
  busy: boolean
  disabled: boolean
  idle: string
  armedText: string
  icon: ReactNode
  onArm: () => void
  onDisarm: () => void
  onConfirm: () => void
}) {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={armed ? onConfirm : onArm}
        className={cn(
          'inline-flex flex-1 items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 motion-reduce:transition-none',
          armed
            ? 'bg-red-500 text-white hover:bg-red-600'
            : 'border border-red-500/30 bg-white text-red-600 hover:bg-red-500/10 dark:bg-neutral-900 dark:text-red-400'
        )}
      >
        <Spin on={busy} icon={icon} />
        {armed ? armedText : idle}
      </button>
      {armed && !busy && (
        <button
          type="button"
          onClick={onDisarm}
          className={cn(ghostBtn, 'px-4')}
        >
          Keep
        </button>
      )}
    </div>
  )
}
